
// =======================================
// PROJECT DETAIL MODAL CONFIGURATION
// =======================================
// Shared types and configuration for the ProjectDetailModal component

// =======================================
// DATA TYPES
// =======================================

/**
 * Team member assigned to a project or stage
 */
export interface ProjectTeamMember {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  type: string;
  is_project_lead?: boolean;
  is_stage_lead?: boolean;
}

/**
 * Engineer available for inspection assignment
 */
/**
 * The tabs of the project/stage detail modal.
 *
 * Named so a page that reopens the modal -- returning from the full-screen
 * document editor, say -- can ask for the tab the user left from.
 */
export type ProjectDetailTab =
  | 'job'
  | 'stage'
  | 'stages'
  | 'team'
  | 'construction'
  | 'settings'
  | 'documents';

/**
 * What the modal's title needs when it is showing a stage: the three strings a
 * stage row already carries, rather than a lookup the modal would have to do.
 */
export interface StageInfo {
  jobNumber: string;
  stageDescription: string;
  parentDescription: string;
}

/**
 * Reopen instructions carried in history state when a route navigates away from
 * an open detail modal, so coming back lands where the user left rather than on
 * a bare register.
 *
 * It carries stageInfo because that is display text derived from the row that
 * was clicked -- a workflow card, a timesheet group -- and is not recoverable
 * from stageId alone once the page has been left and come back to.
 */
export interface ProjectDetailReopen {
  projectId: number;
  stageId?: number | null;
  stageInfo?: StageInfo | null;
  tab: ProjectDetailTab;
}

export interface InspectionEngineer {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

/**
 * Inspection record for construction tracking
 */
export interface Inspection {
  id: number;
  /**
   * The SI number, shown as "SI 3". One sequence per project (or per stage),
   * shared with the site instructions on it, so a record typed into
   * Construction Admin and an instruction issued from the builder can never
   * both be SI 3.
   *
   * For a document-backed row this equals `document.sequence_number` -- read it
   * from here regardless, because `document` comes back null for a viewer whose
   * type is outside the site instruction template's can_view.
   */
  sequence_number: number | null;
  inspection_date: string;
  description: string;
  team_member: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
  };
  created_at: string;
  updated_at: string;
  /**
   * Set when this inspection is the record of an issued site instruction. The
   * document owns the row -- it is written when the instruction is issued and
   * withdrawn if it is reopened -- so Construction Admin shows the document's
   * number and links to it instead of offering Edit/Delete.
   */
  document_id: string | null;
  document: {
    id: string;
    document_number: string;
    sequence_number: number | null;
    title: string | null;
    status: string;
  } | null;
}

/**
 * Form data for adding/editing an inspection
 */
export interface InspectionFormData {
  /** The SI number, as typed. Blank means "allocate the next one". */
  sequence_number: string;
  inspection_date: string;
  team_member_id: string;
  description: string;
}

/**
 * A single PEP QA checklist item (one per qa_type per project/stage)
 */
export interface QaItem {
  qa_type: string;
  required: boolean;
  completion_date: string | null;
}

/**
 * The fixed standard list of RWC QA checklist items, in display order.
 * `qa_type` matches the `rwc_qa_type` enum in the database.
 */
export const RWC_QA_ITEMS: { qa_type: string; label: string }[] = [
  { qa_type: 'block_density_crushing', label: 'Block density & crushing tests' },
  { qa_type: 'mortar_mix_composition', label: 'Mortar mix composition' },
  { qa_type: 'footing_backfill_compaction', label: 'Footing & backfill compaction' },
  { qa_type: 'concrete_strength_testing', label: 'Concrete strength testing' },
  { qa_type: 'concrete_delivery_docket', label: 'Concrete delivery docket' },
];

/**
 * Detailed project data structure for the modal view
 */
export interface ProjectDetail {
  id: number;
  job_number: number | null;
  description: string;
  status: string;
  types: string;
  standard: string | null;
  created_at: string;
  updated_at?: string;
  wall_type: string[] | null; // multi-select; NULL when none picked
  roof_type: string[] | null; // multi-select; NULL when none picked
  floor_type: string[] | null; // multi-select; NULL when none picked
  structure_type: string[] | null; // multi-select; NULL when none picked
  wind_region: string | null;
  site_classification: string | null;
  key_words: string | null;
  po: string | null;
  workflow_status_id?: number | null;
  // Address fields
  street_address: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  mapbox_data: Record<string, string> | null;
  // RWC-specific fields
  retaining_standard: string | null;
  startup_meeting: boolean | null;
  number_of_inspections: number | null;
  requires_qa: boolean | null;
  start_up_completion_date: string | null;
  qa_completion_date: string | null;
  qa_items?: QaItem[];
  // Design PEP fields
  scope_of_works: string | null;
  exclusions: string | null;
  has_civil_scope: boolean | null;
  care_of: string | null;
  drawing_index?: DrawingIndexEntry[];
  risks?: ProjectRisk[];
  programme_items?: ProgrammeItem[];
  region: string | null;
  project_lead: {
    id: number | string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  client: {
    id: number | string;
    company_name: string;
  } | null;
  primary_contact: {
    id: number | string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  head_contractor: {
    client: { id: number | string; company_name: string } | null;
    contact: { id: number | string; first_name: string; last_name: string; full_name: string } | null;
  } | null;
  wall_contractor: {
    client: { id: number | string; company_name: string } | null;
    contact: { id: number | string; first_name: string; last_name: string; full_name: string } | null;
  } | null;
  team_members?: ProjectTeamMember[];
  stages?: ProjectStageWithTeamMembers[];
}

/**
 * Project stage with team members
 */
export interface ProjectDrawing {
  id: number;
  stage_id: number;
  drawing_number: string;
  issue_number: string;
  title: string;
  is_design_standard: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Design PEP — a single drawing index entry (drawing series), per project
 */
export interface DrawingIndexEntry {
  id: number;
  discipline: 'civil' | 'structural';
  title: string | null;
  description: string | null;
  sort_order: number;
}

/**
 * Design PEP — a project risk register entry. Preset risks carry a preset_key
 * (label comes from DESIGN_RISK_ITEMS); custom risks carry a risk_label.
 */
export interface ProjectRisk {
  id: number;
  preset_key: string | null;
  risk_label: string | null;
  rating: string | null;
  mitigation: string | null;
  sort_order: number;
}

/**
 * Design PEP — a programme stage. Standard stages carry a fee phase; custom
 * stages carry a custom_name. `removed` moves a standard stage to the add-bar.
 */
export interface ProgrammeItem {
  id: number;
  phase: string | null;
  discipline: 'civil' | 'structural' | null;
  custom_name: string | null;
  target_date: string | null;
  actual_date: string | null;
  notes: string | null;
  removed: boolean;
  sort_order: number;
}

/**
 * Design PEP — fixed preset project risks (always shown; custom risks can be added).
 */
export const DESIGN_RISK_ITEMS: { preset_key: string; label: string }[] = [
  { preset_key: 'complex_site', label: 'Complex or unusual site conditions' },
  { preset_key: 'tight_timeline', label: 'Tight project timeline' },
  { preset_key: 'remote_location', label: 'Remote / regional location' },
  { preset_key: 'unfamiliar_client', label: 'New or unfamiliar client' },
  { preset_key: 'other', label: 'Other' },
];

/** L / M / H risk rating options */
export const RISK_RATING_OPTIONS: string[] = ['L', 'M', 'H'];

export interface ProjectStageWithTeamMembers {
  id: number;
  stage_id: string;
  stage_description: string;
  job_number: string;
  status: string;
  workflow_status_id: number | null;
  po: string | null;
  // RWC-specific fields
  region: string | null;
  retaining_standard: string | null;
  startup_meeting: boolean | null;
  number_of_inspections: number | null;
  requires_qa: boolean | null;
  start_up_completion_date: string | null;
  qa_completion_date: string | null;
  qa_items?: QaItem[];
  // RWD-specific fields
  site_classification: string | null;
  project_lead: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  head_contractor: {
    client: { id: number | string; company_name: string } | null;
    contact: { id: number | string; first_name: string; last_name: string; full_name: string } | null;
  } | null;
  wall_contractor: {
    client: { id: number | string; company_name: string } | null;
    contact: { id: number | string; first_name: string; last_name: string; full_name: string } | null;
  } | null;
  team_members: ProjectTeamMember[];
  care_of: string | null;
  /** The stage's own contact. Null means it inherits the project's. */
  primary_contact: {
    id: number | string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  created_at: string;
}

/**
 * Form data structure for editing project details
 */
export interface ProjectFormData {
  description: string;
  status: string;
  types: string;
  standard: string;
  project_lead: string;
  client: string;
  primary_contact: string;
  wall_type: string[];
  roof_type: string[];
  floor_type: string[];
  structure_type: string[];
  wind_region: string;
  site_classification: string;
  key_words: string;
  po: string;
  // Address fields
  street_address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  // RWC-specific fields
  retaining_standard: string;
  startup_meeting: boolean;
  number_of_inspections: string;
  requires_qa: boolean;
  region: string;
  // Contractor fields
  head_contractor_client_id: string;
  head_contractor_contact_id: string;
  wall_contractor_client_id: string;
  wall_contractor_contact_id: string;
  // Design PEP fields
  scope_of_works: string;
  exclusions: string;
  has_civil_scope: boolean;
  // Invoice care-of: the owner/paying party the invoice is addressed to. Renders
  // as the top address line, with our client shown below as "c/- {client}".
  care_of: string;
}

/**
 * Form validation errors
 */
export interface FormErrors {
  [key: string]: string | undefined;
}

/**
 * Team member structure for dropdowns
 */
export interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

/**
 * Project stage data structure
 */
export interface ProjectStage {
  id: number;
  parent_project_id: number;
  stage_id: string;
  stage_description: string;
  job_number: string;
  status: string;
  workflow_status_id: number | null;
  care_of: string | null;
  /** The stage's own contact id. Null means it inherits the project's. */
  primary_contact: number | null;
  project_lead_info: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  created_at: string;
  updated_at: string;
}

/**
 * Form data structure for creating/editing stages
 */
export interface StageFormData {
  stage_id: string;
  stage_description: string;
  project_lead?: string;
  workflow_status_id: string;
  care_of?: string;
  /** Contact id as a string; '' inherits the project's contact. */
  primary_contact?: string;
}

// =======================================
// FIELD CONFIGURATION
// =======================================

/**
 * Configuration for modal form fields
 */
export interface ModalFieldConfig {
  type: 'text' | 'select' | 'readonly';
  label: string;
  options?: readonly string[];
  required?: boolean;
  section: 'basic' | 'people' | 'technical' | 'other' | 'settings';
  order: number;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Available project status options
 */
export const PROJECT_STATUS_OPTIONS = [
  'Probable',
  'Design',
  'Construction',
  'Complete',
  'Archived',
  'Unsuccessful'
] as const;

/**
 * Available project job type options
 */
export const PROJECT_JOB_TYPE_OPTIONS = [
  'Design',
  'Advisory',
  'RWC',
  'RWD'
] as const;

export type ProjectJobType = typeof PROJECT_JOB_TYPE_OPTIONS[number];

/**
 * One line per job type saying what choosing it unlocks in the modal. Shown
 * under the type picker on Add Job. Keep in step with the tab and section
 * conditions in ProjectDetailModal/index.tsx -- this is the only place a user
 * is told the difference before they commit to a type.
 */
export const JOB_TYPE_HINTS: Record<ProjectJobType, string> = {
  Design: 'Design details, site, drawing index, risk and programme, Construction Admin, stages.',
  Advisory: 'Design details, site, scope and risk. No drawing index or Construction Admin.',
  RWC: 'Startup meeting, inspections, QA and contractors, Construction Admin. No stages.',
  RWD: 'Site classification and drawing set per stage. No PO.',
};

/**
 * Structure type options for projects: what the building is.
 */
export const PROJECT_STRUCTURE_TYPE_OPTIONS = [
  'Luxury Residential',
  'Residential',
  'Townhouse / Duplex',
  'Short-Stay Accommodation',
  'Apartments',
  'Hotel / Motel',
  'Office',
  'Retail / Hospitality',
  'Car Park',
  'Warehouse',
  'Industrial',
  'Health Care',
  'School / Education',
  'Place of Assembly',
  'Aged Care',
  'Shed / Garage / Carport',
  'Patio / Pergola',
  'Swimming Pool',
  'Retaining Wall',
  'Other Structure',
  'Farm Building'
] as const;

/**
 * The NCC building class(es) each structure type falls under. The class is
 * derived from the structure type for display and never stored, so the two
 * cannot disagree. Farm Building covers both storage (7b) and processing (8).
 */
export const NCC_CLASS_BY_STRUCTURE_TYPE: Record<typeof PROJECT_STRUCTURE_TYPE_OPTIONS[number], readonly string[]> = {
  'Luxury Residential': ['1a'],
  'Residential': ['1a'],
  'Townhouse / Duplex': ['1a'],
  'Short-Stay Accommodation': ['1b'],
  'Apartments': ['2'],
  'Hotel / Motel': ['3'],
  'Office': ['5'],
  'Retail / Hospitality': ['6'],
  'Car Park': ['7a'],
  'Warehouse': ['7b'],
  'Industrial': ['8'],
  'Health Care': ['9a'],
  'School / Education': ['9b'],
  'Place of Assembly': ['9b'],
  'Aged Care': ['9c'],
  'Shed / Garage / Carport': ['10a'],
  'Patio / Pergola': ['10a'],
  'Swimming Pool': ['10b'],
  'Retaining Wall': ['10b'],
  'Other Structure': ['10b'],
  'Farm Building': ['7b', '8'],
};

/** NCC classes in code order, for listing a project's classes. */
const NCC_CLASS_ORDER = ['1a', '1b', '2', '3', '4', '5', '6', '7a', '7b', '8', '9a', '9b', '9c', '10a', '10b', '10c'];

/** The distinct NCC classes a set of structure types falls under, in code order. */
export const nccClassesFor = (structureTypes: readonly string[]): string[] => {
  const classes = new Set<string>();
  structureTypes.forEach(type => {
    (NCC_CLASS_BY_STRUCTURE_TYPE[type as keyof typeof NCC_CLASS_BY_STRUCTURE_TYPE] ?? []).forEach(c => classes.add(c));
  });
  return NCC_CLASS_ORDER.filter(c => classes.has(c));
};

/**
 * Available project standard options
 */
export const PROJECT_STANDARD_OPTIONS = [
  'TE',
  'CAMS',
  'PROGRAMMED'
] as const;

/**
 * Wall type options for projects
 */
export const PROJECT_WALL_TYPE_OPTIONS = [
  'Full Masonry',
  'Masonry Veneer',
  'Reverse Masonry Veneer',
  'Core Filled Blockwork',
  'Timber Stud',
  'Steel Stud',
  'Structural Steel Frame',
  'Precast Concrete',
  'Tilt-Up Concrete',
  'In-situ Concrete',
  'Rammed Earth'
] as const;

/**
 * Roof type options for projects
 */
export const PROJECT_ROOF_TYPE_OPTIONS = [
  'Timber Truss',
  'Steel Truss',
  'Stick Roof',
  'Timber Rafters & Battens',
  'Steel Rafters & Purlins',
  'Portal Frame',
  'Structural Insulated Panel (SIP)',
  'Concrete Slab'
] as const;

/** The Design details fields that are multi-select arrays. */
export type MultiSelectField = 'structure_type' | 'wall_type' | 'roof_type' | 'floor_type';

/**
 * Floor type options for projects
 */
export const PROJECT_FLOOR_TYPE_OPTIONS = [
  'Slab on Ground',
  'Suspended Concrete Slab',
  'Composite Steel Deck',
  'Timber Joists & Bearers',
  'Steel Joists & Bearers'
] as const;

/**
 * Wind region options for projects
 */
export const PROJECT_WIND_REGION_OPTIONS = [
  'A0',
  'A1',
  'A2',
  'A3',
  'A4',
  'A5',
  'B1',
  'B2',
  'C',
  'D'
] as const;

/**
 * Site classification options for projects
 */
export const PROJECT_SITE_CLASSIFICATION_OPTIONS = [
  'A',
  'S',
  'M',
  'M-D',
  'H1',
  'H1-D',
  'H2',
  'H2-D',
  'E'
] as const;

/**
 * Retaining standard options for RWC projects
 */
export const RETAINING_STANDARD_OPTIONS = [
  'Cossill & Webley',
  'Tabec',
  'Other Engineer'
] as const;

/**
 * Region options for RWC projects
 */
export const RWC_REGION_OPTIONS = [
  'South',
  'North',
  'North East',
  'South East'
] as const;

/**
 * The "no contact chosen" entry of a stage's primary-contact dropdown. A stage
 * with no contact of its own uses the project's, so the entry says whose.
 */
export const stageContactInheritLabel = (projectContactName: string | null | undefined): string =>
  projectContactName ? `Inherit from project (${projectContactName})` : 'Inherit from project (none set)';

/**
 * Default stage form data
 */
export const DEFAULT_STAGE_FORM_DATA: StageFormData = {
  stage_id: '',
  stage_description: '',
  project_lead: '',
  workflow_status_id: '',
  care_of: '',
  primary_contact: ''
};

/**
 * Modal form field configuration for project details
 */
export const projectModalFields: Record<string, ModalFieldConfig> = {
  // Basic Information Section
  // Note: job_number is displayed in the modal title, not as a form field
  description: {
    type: 'text',
    label: 'Description',
    required: true,
    section: 'basic',
    order: 1
  },
  status: {
    type: 'select',
    label: 'Status',
    options: PROJECT_STATUS_OPTIONS,
    required: true,
    section: 'basic',
    order: 2
  },
  types: {
    type: 'select',
    label: 'Type',
    options: PROJECT_JOB_TYPE_OPTIONS,
    required: true,
    section: 'basic',
    order: 3
  },
  standard: {
    type: 'select',
    label: 'Standard',
    options: PROJECT_STANDARD_OPTIONS,
    section: 'basic',
    order: 4
  },
  
  // People Section
  // Note: project_lead is now in the Team Members tab
  client: {
    type: 'select',
    label: 'Client',
    section: 'people',
    order: 1
  },
  
  // Technical Section
  wind_region: {
    type: 'select',
    label: 'Wind Region',
    options: PROJECT_WIND_REGION_OPTIONS,
    section: 'technical',
    order: 3
  },
  site_classification: {
    type: 'select',
    label: 'Site Classification',
    options: PROJECT_SITE_CLASSIFICATION_OPTIONS,
    section: 'technical',
    order: 4
  },
  
  // Other Section
  // key_words has been replaced by tags (see TagSelector component)
  
  // Settings Section
  created_at: {
    type: 'readonly',
    label: 'Created',
    section: 'settings',
    order: 1
  },
  updated_at: {
    type: 'readonly',
    label: 'Updated',
    section: 'settings',
    order: 2
  }
};

